import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, useReducer, type ReactNode } from 'react';
import { queryKeys } from '../../api/keys';
import { useRetryActivity } from '../../api/mutations';
import { errorMessage } from '../../lib/api';
import { createItems, isPolling, uploadReducer, type UploadItem } from './uploadQueue';
import { startUploads } from './uploadRunner';
import { UploadStatusPoller } from './UploadStatusPoller';

export interface UploadQueueValue {
  items: UploadItem[];
  addFiles: (files: File[]) => void;
  retry: (id: string) => void;
  clearFinished: () => void;
}

const UploadQueueContext = createContext<UploadQueueValue | null>(null);

export function useUploadQueue(): UploadQueueValue {
  const value = useContext(UploadQueueContext);
  if (value === null) throw new Error('useUploadQueue must be used inside UploadQueueProvider');
  return value;
}

export function UploadQueueProvider({ children }: { children: ReactNode }) {
  const [items, dispatch] = useReducer(uploadReducer, []);
  const queryClient = useQueryClient();
  const retryActivity = useRetryActivity();

  const addFiles = useCallback((files: File[]) => {
    const created = createItems(files, () => crypto.randomUUID());
    dispatch({ type: 'add', items: created });
    void startUploads(created.filter((item) => item.phase === 'requesting'), dispatch);
  }, []);

  const retry = useCallback((id: string) => {
    const item = items.find((candidate) => candidate.id === id);
    if (item === undefined) return;
    dispatch({ type: 'retry', id });
    if (item.activityId === null || item.key === null) {
      void startUploads([item], dispatch);
      return;
    }
    queryClient.removeQueries({ queryKey: queryKeys.upload(item.key) });
    void retryActivity.mutateAsync(item.activityId).then(
      () => dispatch({ type: 'resume', id }),
      (error: unknown) => dispatch({ type: 'error', id, message: errorMessage(error) }),
    );
  }, [items, queryClient, retryActivity.mutateAsync]);

  const clearFinished = useCallback(() => dispatch({ type: 'clearFinished' }), []);
  const value = useMemo(() => ({ items, addFiles, retry, clearFinished }), [items, addFiles, retry, clearFinished]);

  return (
    <UploadQueueContext.Provider value={value}>
      {children}
      {items.filter(isPolling).map((item) => (
        <UploadStatusPoller key={item.id} id={item.id} uploadKey={item.key} dispatch={dispatch} />
      ))}
    </UploadQueueContext.Provider>
  );
}
